import { Target, Clock } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Theme } from '@/shared/constants/themes';

interface Action {
    id: number;
    text: string;
    type: string;
}

interface LiveActionsProps {
    actions?: Action[];
    timestamp?: string | any; // ISO timestamp or Firestore Timestamp
    theme?: Theme;
}

export const LiveActions: React.FC<LiveActionsProps> = ({ actions = [], timestamp, theme }) => {
    const themeColors = theme?.colors;
    const [isNew, setIsNew] = useState(false);
    const prevTimestampRef = useRef<string | any>(timestamp);

    // Detect when actions change
    useEffect(() => {
        if (timestamp && timestamp !== prevTimestampRef.current) {
            setIsNew(true);
            prevTimestampRef.current = timestamp;

            // Remove animation after 3 seconds
            const timer = setTimeout(() => setIsNew(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [timestamp]);

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'jeu': return '🎮';
            case 'jp': return '⚡';
            case 'interaction': return '💬';
            case 'product': return '👕';
            case 'style': return '✨';
            case 'engagement': return '🔥';
            default: return '📌';
        }
    };

    const formatTimestamp = (ts?: string | any) => {
        if (!ts) return '';

        // Handle Firestore Timestamp object
        if (ts?.toDate) {
            return ts.toDate().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        }

        // Handle ISO string
        if (typeof ts === 'string') {
            const date = new Date(ts);
            if (!isNaN(date.getTime())) {
                return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            }
        }

        return '';
    };

    return (
        <div 
            className={`rounded-lg shadow-sm border overflow-hidden transition-all duration-300 ${isNew ? 'ring-2 shadow-lg' : ''}`}
            style={{
                backgroundColor: themeColors?.surface || '#FFFFFF',
                borderColor: isNew ? themeColors?.primary || '#9333EA' : (themeColors?.border || '#E5E7EB'),
                ringColor: themeColors?.primary || '#9333EA'
            }}
        >
            {/* Header */}
            <div 
                className={`px-4 py-3 border-b transition-all duration-300`}
                style={{
                    background: isNew 
                        ? `linear-gradient(to right, ${themeColors?.primaryLight || '#A78BFA'}20, ${themeColors?.accentLight || '#2DD4BF'}20)`
                        : `linear-gradient(to right, ${themeColors?.primaryLight || '#A78BFA'}15, ${themeColors?.accentLight || '#2DD4BF'}15)`,
                    borderBottomColor: themeColors?.border || '#E5E7EB'
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Target 
                            size={16} 
                            className={isNew ? 'animate-pulse' : ''}
                            style={{ color: themeColors?.primary || '#9333EA' }}
                        />
                        <h3 
                            className="text-sm font-semibold"
                            style={{ color: themeColors?.foreground || '#374151' }}
                        >
                            Actions conseillées
                        </h3>
                        {isNew && (
                            <span 
                                className="text-xs font-bold px-2 py-0.5 rounded-full animate-bounce"
                                style={{
                                    color: themeColors?.primary || '#9333EA',
                                    backgroundColor: themeColors?.primaryLight || '#F3E8FF'
                                }}
                            >
                                NOUVEAU
                            </span>
                        )}
                    </div>
                    {timestamp && (
                        <div 
                            className="flex items-center gap-1 text-xs"
                            style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                        >
                            <Clock size={12} />
                            <span>{formatTimestamp(timestamp)}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions List */}
            <div className="p-4 space-y-2">
                {actions.length === 0 ? (
                    <p 
                        className="text-xs italic text-center py-4"
                        style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                    >
                        Si tu ne veux pas lire les phrases, fais ça 💡
                    </p>
                ) : (
                    actions.map((action, index) => (
                        <div
                            key={action.id}
                            className={`flex items-start gap-2.5 rounded-lg p-3 border transition-all ${isNew ? 'animate-slideIn' : ''}`}
                            style={{
                                animationDelay: `${index * 100}ms`,
                                backgroundColor: themeColors?.backgroundSecondary || '#F9FAFB',
                                borderColor: themeColors?.border || '#E5E7EB'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.primary || '#9333EA';
                                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.border || '#E5E7EB';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <span className="text-lg flex-shrink-0">{getTypeIcon(action.type)}</span>
                            <p 
                                className="text-sm font-medium leading-snug flex-1"
                                style={{ color: themeColors?.foreground || '#111827' }}
                            >
                                {action.text}
                            </p>
                        </div>
                    ))
                )}
            </div>

            <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.4s ease-out forwards;
        }
      `}</style>
        </div>
    );
};
